/**
 * Converts the user-facing UTC calendar date (yyyy-MM-dd) into the SQL
 * FOR SYSTEM_TIME instant: start of the next UTC day (end of the selected day).
 * Mirrors TalentToolbox.Analytics.Core.Helpers.PointInTimeNormaliser.
 */
export const POINT_IN_TIME_DATE_FORMAT = "yyyy-MM-dd";

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * @returns next UTC midnight after the date, or null when live (null/empty input).
 * @throws when the string is non-empty but not a valid yyyy-MM-dd calendar date.
 */
export function getPointInTimeUtc(pointInTimeDate: string | null | undefined): Date | null {
  if (pointInTimeDate == null || pointInTimeDate.trim() === "") {
    return null;
  }

  const trimmed = pointInTimeDate.trim();
  const match = DATE_RE.exec(trimmed);
  if (!match) {
    throw new Error(`Point-in-time date must be '${POINT_IN_TIME_DATE_FORMAT}' or empty.`);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utc = new Date(Date.UTC(year, month - 1, day));

  // Reject non-canonical calendar dates (e.g. 2026-02-30 → rolls to March).
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    throw new Error(`Point-in-time date must be '${POINT_IN_TIME_DATE_FORMAT}' or empty.`);
  }

  utc.setUTCDate(utc.getUTCDate() + 1);
  return utc;
}

/** Same as getPointInTimeUtc but returns false for invalid non-empty input. */
export function tryGetPointInTimeUtc(
  pointInTimeDate: string | null | undefined
): { ok: true; utc: Date | null } | { ok: false } {
  try {
    return { ok: true, utc: getPointInTimeUtc(pointInTimeDate) };
  } catch {
    return { ok: false };
  }
}

/**
 * Canonical yyyy-MM-dd for FilterJson, or null when live / empty / invalid.
 */
export function normalisePointInTimeDateForDocument(
  pointInTimeDate: string | null | undefined
): string | null {
  if (pointInTimeDate == null || pointInTimeDate.trim() === "") {
    return null;
  }
  const trimmed = pointInTimeDate.trim();
  const parsed = tryGetPointInTimeUtc(trimmed);
  return parsed.ok && parsed.utc != null ? trimmed : null;
}
